import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import type {
  ParentState,
  HealthItem,
  ReactionEmoji,
  CommentReply,
  Comment,
  HistoryEntry,
} from "./sharedTypes";

export type { ParentState, HealthItem, Comment, HistoryEntry } from "./sharedTypes";

/** Extract plain text from an HTML string. Used for health checks where the
 *  field may contain rich-text HTML (e.g. from the Tiptap editor). Uses
 *  DOMParser when available (browser / jsdom), otherwise falls back to an
 *  empty-string so that the health flag is not erroneously suppressed. */
const getHtmlTextContent = (html: string): string => {
  if (typeof DOMParser !== "undefined") {
    return (
      new DOMParser().parseFromString(html, "text/html").body.textContent ?? ""
    );
  }
  return "";
};

export type StoryType = "Engineering";
export type EngineeringSubtype =
  | "Frontend"
  | "Backend"
  | "Quality"
  | "DevOps"
  | "Fullstack";

/** Health issue definitions checked against a Story. */
export const STORY_HEALTH_DEFINITIONS: Array<{
  id: string;
  reason: string;
  score: number;
}> = [
  { id: "no_description", reason: "Story has no description", score: 20 },
  { id: "no_points", reason: "Story has no story points assigned", score: 15 },
  {
    id: "no_acceptance_criteria",
    reason: "Story has no acceptance criteria",
    score: 15,
  },
  {
    id: "no_epic",
    reason: "Story is not assigned to an epic",
    score: 15,
  },
];

export interface Story {
  id: string;
  title: string;
  description: string;
  type: StoryType;
  engineeringSubtype?: EngineeringSubtype;
  points: number;
  acceptanceCriteria?: string;
  /** The id of the parent Epic this story belongs to, or null/undefined for orphan stories. */
  epicId?: string | null;
  /** The id of the Sprint this story is assigned to, or null/undefined if unassigned. */
  sprintId?: string | null;
  /** High-level lifecycle stage: Open, In Progress, or Closed. */
  parentState?: ParentState;
  /** Configurable child status within the parentState (e.g. "Blocked", "Ready"). */
  status?: string;
  /** Map of health item ID → acknowledged flag for this story. */
  acknowledgedHealthItems?: Record<string, boolean>;
  /** Comments posted on this story. */
  comments?: Comment[];
  /** Saved change history for this story (newest first). */
  history?: HistoryEntry[];
}

/** Compute the active health items for a story based on its current state.
 *  Optional `storyWeights` overrides the default scores from STORY_HEALTH_DEFINITIONS. */
export const computeStoryHealthItems = (
  story: Story,
  storyWeights?: Record<string, number>,
): HealthItem[] => {
  const acked = story.acknowledgedHealthItems ?? {};
  const defMap = new Map(
    STORY_HEALTH_DEFINITIONS.map((d) => [
      d.id,
      { ...d, score: storyWeights?.[d.id] ?? d.score },
    ]),
  );
  const push = (id: string): void => {
    const def = defMap.get(id);
    if (def) {
      items.push({ ...def, acknowledged: !!acked[id] });
    }
  };
  const items: HealthItem[] = [];

  if (!story.description?.trim()) push("no_description");
  if (!story.points) push("no_points");
  if (
    !story.acceptanceCriteria ||
    !getHtmlTextContent(story.acceptanceCriteria).trim()
  )
    push("no_acceptance_criteria");

  return items;
};

/** Returns a 0-100 health percentage for a single story based on its
 *  unacknowledged health items.  100 = fully healthy, 0 = maximum penalty.
 *
 *  Optional `storyWeights` overrides the default scores from STORY_HEALTH_DEFINITIONS. */
export const getStoryHealthPercent = (
  story: Story,
  storyWeights?: Record<string, number>,
): number => {
  const maxPenalty = STORY_HEALTH_DEFINITIONS.reduce(
    (s, d) => s + (storyWeights?.[d.id] ?? d.score),
    0,
  );
  if (maxPenalty === 0) return 100;
  const penalty = computeStoryHealthItems(story, storyWeights)
    .filter((i) => !i.acknowledged)
    .reduce((sum, i) => sum + i.score, 0);
  return Math.max(
    0,
    Math.min(100, Math.round(100 - (penalty / maxPenalty) * 100)),
  );
};

// ---------------------------------------------------------------------------
// Stories slice
// ---------------------------------------------------------------------------

interface StoriesState {
  /** Stories that are not assigned to any epic. */
  standaloneStories: Story[];
}

const initialState: StoriesState = {
  standaloneStories: [],
};

const storiesSlice = createSlice({
  name: "stories",
  initialState,
  reducers: {
    // ── Standalone story CRUD ──────────────────────────────────────────────

    addStandaloneStory: (state, action: PayloadAction<Story>) => {
      state.standaloneStories.push(action.payload);
    },

    updateStandaloneStory: (state, action: PayloadAction<Story>) => {
      const idx = state.standaloneStories.findIndex(
        (s) => s.id === action.payload.id,
      );
      if (idx !== -1) state.standaloneStories[idx] = action.payload;
    },

    deleteStandaloneStory: (state, action: PayloadAction<string>) => {
      state.standaloneStories = state.standaloneStories.filter(
        (s) => s.id !== action.payload,
      );
    },

    setStandaloneStories: (state, action: PayloadAction<Story[]>) => {
      state.standaloneStories = action.payload;
    },

    // ── Story health acknowledge ───────────────────────────────────────────

    /** Toggle the acknowledged flag for a story-level health item.
     *  When epicId is falsy the action targets standaloneStories;
     *  when epicId is provided it is handled in epicsSlice via extraReducers. */
    toggleStoryHealthAcknowledge: (
      state,
      action: PayloadAction<{
        epicId: string;
        storyId: string;
        healthItemId: string;
      }>,
    ) => {
      if (!action.payload.epicId) {
        const story = state.standaloneStories.find(
          (s) => s.id === action.payload.storyId,
        );
        if (story) {
          const acked = story.acknowledgedHealthItems ?? {};
          story.acknowledgedHealthItems = {
            ...acked,
            [action.payload.healthItemId]:
              !acked[action.payload.healthItemId],
          };
        }
      }
    },

    // ── Story comments (standalone) ────────────────────────────────────────

    addStoryComment: (
      state,
      action: PayloadAction<{
        epicId: string;
        storyId: string;
        comment: Omit<Comment, "id" | "createdAt" | "reactions" | "replies">;
      }>,
    ) => {
      if (!action.payload.epicId) {
        const story = state.standaloneStories.find(
          (s) => s.id === action.payload.storyId,
        );
        if (story) {
          if (!story.comments) story.comments = [];
          story.comments.push({
            id: uuidv4(),
            ...action.payload.comment,
            createdAt: new Date().toISOString(),
            reactions: [],
            replies: [],
          });
        }
      }
    },

    updateStoryComment: (
      state,
      action: PayloadAction<{
        epicId: string;
        storyId: string;
        commentId: string;
        content: string;
      }>,
    ) => {
      if (!action.payload.epicId) {
        const story = state.standaloneStories.find(
          (s) => s.id === action.payload.storyId,
        );
        if (story) {
          const comment = (story.comments ?? []).find(
            (c) => c.id === action.payload.commentId,
          );
          if (comment) {
            comment.content = action.payload.content;
            comment.updatedAt = new Date().toISOString();
          }
        }
      }
    },

    deleteStoryComment: (
      state,
      action: PayloadAction<{
        epicId: string;
        storyId: string;
        commentId: string;
      }>,
    ) => {
      if (!action.payload.epicId) {
        const story = state.standaloneStories.find(
          (s) => s.id === action.payload.storyId,
        );
        if (story && story.comments) {
          story.comments = story.comments.filter(
            (c) => c.id !== action.payload.commentId,
          );
        }
      }
    },

    toggleStoryCommentReaction: (
      state,
      action: PayloadAction<{
        epicId: string;
        storyId: string;
        commentId: string;
        emoji: ReactionEmoji;
        userId: string;
      }>,
    ) => {
      if (!action.payload.epicId) {
        const story = state.standaloneStories.find(
          (s) => s.id === action.payload.storyId,
        );
        if (story) {
          const comment = (story.comments ?? []).find(
            (c) => c.id === action.payload.commentId,
          );
          if (comment) {
            const reaction = comment.reactions.find(
              (r) => r.emoji === action.payload.emoji,
            );
            if (!reaction) {
              comment.reactions.push({
                emoji: action.payload.emoji,
                userIds: [action.payload.userId],
              });
            } else {
              const idx = reaction.userIds.indexOf(action.payload.userId);
              if (idx === -1) {
                reaction.userIds.push(action.payload.userId);
              } else {
                reaction.userIds.splice(idx, 1);
                if (reaction.userIds.length === 0) {
                  comment.reactions = comment.reactions.filter(
                    (r) => r.emoji !== action.payload.emoji,
                  );
                }
              }
            }
          }
        }
      }
    },

    addStoryCommentReply: (
      state,
      action: PayloadAction<{
        epicId: string;
        storyId: string;
        commentId: string;
        reply: Omit<CommentReply, "id" | "createdAt">;
      }>,
    ) => {
      if (!action.payload.epicId) {
        const story = state.standaloneStories.find(
          (s) => s.id === action.payload.storyId,
        );
        if (story) {
          const comment = (story.comments ?? []).find(
            (c) => c.id === action.payload.commentId,
          );
          if (comment) {
            comment.replies.push({
              id: uuidv4(),
              ...action.payload.reply,
              createdAt: new Date().toISOString(),
            });
          }
        }
      }
    },

    updateStoryCommentReply: (
      state,
      action: PayloadAction<{
        epicId: string;
        storyId: string;
        commentId: string;
        replyId: string;
        content: string;
      }>,
    ) => {
      if (!action.payload.epicId) {
        const story = state.standaloneStories.find(
          (s) => s.id === action.payload.storyId,
        );
        if (story) {
          const comment = (story.comments ?? []).find(
            (c) => c.id === action.payload.commentId,
          );
          if (comment) {
            const reply = comment.replies.find(
              (r) => r.id === action.payload.replyId,
            );
            if (reply) {
              reply.content = action.payload.content;
              reply.updatedAt = new Date().toISOString();
            }
          }
        }
      }
    },

    deleteStoryCommentReply: (
      state,
      action: PayloadAction<{
        epicId: string;
        storyId: string;
        commentId: string;
        replyId: string;
      }>,
    ) => {
      if (!action.payload.epicId) {
        const story = state.standaloneStories.find(
          (s) => s.id === action.payload.storyId,
        );
        if (story) {
          const comment = (story.comments ?? []).find(
            (c) => c.id === action.payload.commentId,
          );
          if (comment) {
            comment.replies = comment.replies.filter(
              (r) => r.id !== action.payload.replyId,
            );
          }
        }
      }
    },
  },
});

export const {
  addStandaloneStory,
  updateStandaloneStory,
  deleteStandaloneStory,
  setStandaloneStories,
  toggleStoryHealthAcknowledge,
  addStoryComment,
  updateStoryComment,
  deleteStoryComment,
  toggleStoryCommentReaction,
  addStoryCommentReply,
  updateStoryCommentReply,
  deleteStoryCommentReply,
} = storiesSlice.actions;

export default storiesSlice.reducer;
